import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfStyles } from '../../..';
import { getLocalDate } from '../../../../config/utils/dateHelpers';
import { InvoiceInterface } from '../../../../interfaces/invoice.interface';
import { RECEIVING_NOTE_PURPOSE } from '../../../../interfaces/myData.interface';
import { isQuantityReceiptForm } from '../../../../services/utils/myDataUtils';

interface Props {
  doc: InvoiceInterface;
}

export const PdfExtraInfo = ({ doc }: Props) => {
  const { t } = useTranslation();

  const getShipToValue = useCallback(() => {
    return `${doc?.shipTo} ${doc?.shipToCity || ''} ${doc?.shipToPostalCode || ''}`;
  }, [doc?.shipTo, doc?.shipToCity, doc?.shipToPostalCode]);

  const getDocPurpose2 = useCallback(() => {
    if (doc?.docPurpose2?.name) {
      return ` - ${t(`DOC_PURPOSES.${doc?.docPurpose2?.name}`)}`;
    }
  }, [doc?.docPurpose2?.name, t]);

  return (
    <View style={styles.columnCenter}>
      <View style={styles.center}>
        <Text style={pdfStyles.smallTitle}>{t('PDF.DOC_DATA')}</Text>
      </View>
      <View style={[styles.container, { width: 275 }]}>
        <View style={styles.row}>
          <Text style={[pdfStyles.smallerTitle, { paddingRight: 2 }]}>{t('PDF.DOC_PURPOSE')}:</Text>
          <Text style={pdfStyles.fontNormal7}>
            {t(`DOC_PURPOSES.${doc?.docPurpose?.name}`)}
            {getDocPurpose2()}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={[pdfStyles.smallerTitle, { paddingRight: 2 }]}>{t('PDF.PAYMENT_TYPE')}:</Text>
          <Text style={pdfStyles.fontNormal7}>{t(`PAYMENT_TYPES.${doc?.paymentType?.name}`)}</Text>
        </View>
        <View style={[styles.row]}>
          <View style={styles.row}>
            <Text style={[pdfStyles.smallerTitle, { paddingRight: 2 }]}>{t('PDF.DELIVERY_TYPE')}:</Text>
            <Text style={pdfStyles.fontNormal7}>{doc?.shipping}</Text>
          </View>
        </View>
        <View style={styles.row}>
          <Text style={[pdfStyles.smallerTitle, { paddingRight: 2 }]}>{t('PDF.DELIVERY_TIME')}:</Text>
          <Text style={pdfStyles.fontNormal7}>{doc?.shipDate ? getLocalDate(doc?.shipDate) : ' - '}</Text>
        </View>
        <View style={styles.row}>
          <Text style={[pdfStyles.smallerTitle, { paddingRight: 2 }]}>{t('PDF.DELIVERY_PLACE')}:</Text>
          <Text style={pdfStyles.fontNormal7}>{doc?.shipFrom}</Text>
        </View>
        <View style={styles.row}>
          <Text style={[pdfStyles.smallerTitle, styles.placeholder, { paddingRight: 2 }]}>
            {t('PDF.DELIVERY_RECEIVE')}:
          </Text>
          <Text style={[pdfStyles.fontNormal7, pdfStyles.maxWidth180, pdfStyles.maxLines2]}>{getShipToValue()}</Text>
        </View>
        <View style={styles.row}>
          <Text style={[pdfStyles.smallerTitle, { paddingRight: 2 }]}>{t('PDF.VAT_REGIME')}:</Text>
          <Text style={pdfStyles.fontNormal7}>{t(`VAT_REGIME.${doc?.vatRegime?.name}`)}</Text>
        </View>
        {isQuantityReceiptForm(doc.documentType?.aadeCode) && doc.receivingNotePurpose != null ? (
          <View style={styles.row}>
            <Text style={[pdfStyles.smallerTitle, { paddingRight: 2 }]}>{t('PDF.RECEIVING_NOTE_PURPOSE')}:</Text>
            <Text style={[pdfStyles.fontNormal7, pdfStyles.maxWidth200, pdfStyles.maxLines2]}>
              {doc.receivingNotePurpose === RECEIVING_NOTE_PURPOSE.OTHER
                ? doc.otherReceivingNotePurposeTitle
                : t(`SALE.RECEIVING_NOTE_PURPOSE_${doc.receivingNotePurpose}`)}
            </Text>
          </View>
        ) : (
          <View style={styles.row}>
            <Text style={[pdfStyles.smallerTitle, { paddingRight: 2 }]}>{t('PDF.VAT_EXCEPTION_CAT')}:</Text>
            <Text style={[pdfStyles.fontNormal7, pdfStyles.maxWidth200, pdfStyles.maxLines2]}>
              {doc?.vatExceptionCategoryId ? t(`VAT_EXCEPTION_CAT.${doc?.vatExceptionCategory?.name}`) : ''}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  start: {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  center: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  columnCenter: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  container: {
    borderWidth: 1,
    borderRadius: 6,
    flexDirection: 'column',
    padding: 2,
  },
  placeholder: {
    width: 84,
  },
});
